import React from 'react';
import moment from 'moment';
import axios from 'axios';
import qs from 'qs';
import ReactGA from 'react-ga';
import { loadReCaptcha } from 'react-recaptcha-v3';
import { withCookies } from 'react-cookie';
import { withSnackbar } from 'notistack';
import { withTranslation } from 'react-i18next';
import { connect } from 'react-redux';
import Button from '@material-ui/core/Button';
import MobileApp from './MobileApp';
import DesktopApp from './DesktopApp';
import DateOfBirthDialog from './parts/DateOfBirthDialog';
import AlertDialog from './parts/AlertDialog';
import store from '../store/index';
import { setData, setDataBatch } from '../actions/index';
import {
  getDefaultViewForPupil, getSessionCredentials, logoutUser,
  setSessionCredentials, saveUserToStore,
} from '../helpers';
import i18n from '../i18n/index';
import LogoutDialog from './parts/Dialogs/LogoutDialog';
import ErrorBoundary from './parts/Error/ErrorBoundary';

// Moment
moment.updateLocale('en', {
  week: {
    dow: 1,
  },
});

// Axios
axios.defaults.headers.common['X-Requested-With'] = 'XMLHttpRequest';
axios.defaults.headers.common['Accept'] = 'application/json, text/javascript, */*; q=0.01';

const mapStateToProps = state => {
  return {
    user: state.user,
  };
};

function mapDispatchToProps(dispatch) {
  return {
    setData: (data, type) => dispatch(setData(data, type)),
    setDataBatch: (data) => dispatch(setDataBatch(data)),
  };
}

/**
 * TODO:
 * Use react-redux, instead of native redux
 * Also, implement the react-router instead of the hash-hased routing
 * Also, use redux instead of prop-drilling
 * Also, remove all the useSuspense()'s from the codebase
 */
class App extends React.Component {
  constructor(props) {
    super(props);

    this.isIE = /Trident|MSIE/.test(navigator.userAgent);
    this.oldLocationHash = location.hash;

    this.state = {
      isLoggedIn: false,
      selectedPupil: null,
      selectedPupilIndex: 0,
      view: 'loading',
      isLogoutDialogOpen: false,
      isDobDialogOpen: false,
      isAlertDialogOpen: false,
      alertDialogContent: '',
    };

    this.appVersion = null;
    this.appVersionUpdateInfoShown = false;
    this.pingInterval = 180000;
    this.lastPingTime = null;
    this.pushNotificationData = null;

    this.onHashChange = this.onHashChange.bind(this);
    this.onDashboardClick = this.onDashboardClick.bind(this);
    this.onLogoutClick = this.onLogoutClick.bind(this);
    this.onLogoutDialogClose = this.onLogoutDialogClose.bind(this);
    this.onLogoutDialogButtonClick = this.onLogoutDialogButtonClick.bind(this);
    this.onMyCodeClick = this.onMyCodeClick.bind(this);
    this.onDateOfBirthConfirm = this.onDateOfBirthConfirm.bind(this);
    this.onNewMessage = this.onNewMessage.bind(this);
    this.onGetThread = this.onGetThread.bind(this);
    this.onGetPushNotificationMessage = this.onGetPushNotificationMessage.bind(this);
    this.addSelectedPupilMessagesCount = this.addSelectedPupilMessagesCount.bind(this);
    this.checkLoggedInState = this.checkLoggedInState.bind(this);
    this.checkFabButton = this.checkFabButton.bind(this);
    this.updateUserSession = this.updateUserSession.bind(this);
    this.doPing = this.doPing.bind(this);
    this.prepareViewState = this.prepareViewState.bind(this);
  }

  componentDidMount() {
    // TODO: Get rid of this once recaptcha works on the apps again
    //loadReCaptcha(googleRecaptchaSiteKey);

    window.addEventListener('messaging:new-message', this.onNewMessage);
    window.addEventListener('messaging:get-thread', this.onGetThread);

    this.checkLoggedInState(true);
    this.storeSubscription = store.subscribe(() => {
      this.checkLoggedInState();
    });

    // Log the initial page view
    if (window.location.hash) {
      const hashSplit = window.location.hash.split(',');
      if (hashSplit.length === 2) {
        ReactGA.pageview('/mobile/' + appType +  '/' + hashSplit[1] + '/' + hashSplit[0].replace('#', ''));
      }
    }

    // Communication from the react native app
    window.addEventListener('message', (event) => {
      if (
        !event.data ||
        !event.data.action
      ) {
        return;
      }

      const action = event.data.action;
      const data = event.data.data;

      if (action === 'pushnotificationregister') {
        this.pushNotificationData = data;
      } else if (action === 'pushnotificationmessage') {
        this.onGetPushNotificationMessage(data);
      }
    });

    window.addEventListener('hashchange', this.onHashChange, false);
    if (this.isIE) {
      this.hashChangeInterval = setInterval(() => {
        if (this.oldLocationHash !== location.hash) {
          this.onHashChange({
            newURL: location.href,
          });

          this.oldLocationHash = location.hash;
        }
      }, 100);
    }

    this.sessionIdInterceptor = axios.interceptors.response.use((response) => {
      this.updateUserSession(response);

      return response;
    }, (error) => {
      return Promise.reject(error)
    });

    this.doPingInterval = setInterval(() => {
      const storeState = store.getState();
      if (
        storeState.user &&
        appType === 'student' &&
        storeState.selectedPupil &&
        storeState.selectedPupil.defaultView !== 'messages'
      ) {
        this.doPing(true, storeState.user);
      }
    }, this.pingInterval);
  }

  componentWillUnmount() {
    window.removeEventListener('messaging:new-message', this.onNewMessage);
    window.removeEventListener('messaging:get-thread', this.onGetThread);

    if (this.storeSubscription) {
      this.storeSubscription();
    }

    window.removeEventListener('hashchange', this.onHashChange, false);
    if (this.isIE) {
      clearInterval(this.hashChangeInterval);
    }

    if (this.sessionIdInterceptor) {
      axios.interceptors.request.eject(this.sessionIdInterceptor);
    }

    clearInterval(this.doPingInterval);
  }

  // TODO
  // Do a complete refactor of this function below or yet better:
  // implement react router to handle the routing properly
  onHashChange(e) {
    if (!e.newURL) {
      return;
    }

    if (this.hashChangeInProgress) {
      return;
    }

    this.hashChangeInProgress = true;

    const storeState = store.getState();
    let selectedPupil = null;

    let newHash = '';
    if (e.newURL.indexOf('#')) {
      newHash = e.newURL.split('#')[1];
    }

    if (newHash) {
      const hashSplit = newHash.split(',');
      storeState.pupils.map((pupil) => {
        if (parseInt(hashSplit[0]) === pupil.id) {
          selectedPupil = pupil;
          selectedPupil.defaultView = hashSplit[1];
        }
      });

      if (
        storeState.openModal !== false &&
        typeof hashSplit[2] === 'undefined'
      ) {
        store.dispatch(setData(false, 'openModal'));
      } else if (
        storeState.openModal === false &&
        typeof hashSplit[2] !== 'undefined'
      ) {
        store.dispatch(setData(hashSplit[2], 'openModal'));
      }

      if (
        appType === 'student' &&
        storeState.selectedPupil &&
        selectedPupil === null
      ) {
        selectedPupil = storeState.selectedPupil;
        selectedPupil.defaultView = hashSplit[1] ?? getDefaultViewForPupil(selectedPupil);
      }
    } else {
      if (appType === 'parent') {
        this.setState({
          view: 'dashboard',
        });
      } else if (appType === 'student' && storeState.selectedPupil) {
        selectedPupil = storeState.selectedPupil;
        selectedPupil.defaultView = getDefaultViewForPupil(selectedPupil);
      }
    }

    if (selectedPupil) {
      ReactGA.pageview('/mobile/' + appType +  '/' + selectedPupil.defaultView + '/' + selectedPupil.id);
    } else {
      ReactGA.pageview('/mobile/' + appType);
    }

    store.dispatch(setDataBatch({
      selectedPupil,
      messagesSelectedThread: null,
      messagesMultipleMessage: false,
      messagesMultipleMessageSelectedIds: {},
    }));

    this.checkFabButton(selectedPupil);

    setTimeout(() => {
      this.hashChangeInProgress = false;
    });
  }

  onDashboardClick() {
    this.setState({
      isLoggedIn: true,
      view: 'dashboard',
    });
    store.dispatch(setData(null, 'selectedPupil'));
  }

  onLogoutClick() {
    this.setState({
      isLogoutDialogOpen: true,
    });
  }

  onLogoutDialogClose() {
    this.setState({
      isLogoutDialogOpen: false,
    });
  }

  onLogoutDialogButtonClick() {
    const {
      cookies,
    } = this.props;

    logoutUser(cookies, this.pushNotificationData);

    this.setState({
      isLogoutDialogOpen: false,
    });
  }

  onMyCodeClick() {
    if (this.props.user?.has_birthday) {
      this.setState({
        isDobDialogOpen: true,
      });
    } else {
      this.onDateOfBirthConfirm('');
    }
  }

  onDateOfBirthConfirm(date) {
    const {
      t,
      enqueueSnackbar,
    } = this.props;

    axios
      .post(`${apiUrl}/getcode`, qs.stringify({
        date: date,
      }))
      .then((response) => {
        if (response.data.error) {
          enqueueSnackbar(response.data.error, {
            variant: 'error',
            autoHideDuration: 2000,
          });
        } else {
          this.setState({
            isAlertDialogOpen: true,
            alertDialogContent: (
              <React.Fragment>
                <div style={{
                    minWidth: 300,
                    textAlign: 'center',
                    fontSize: 22,
                  }}>
                  <p style={{
                    fontSize: 30,
                    textTransform: 'uppercase',
                    letterSpacing: 5
                  }}>{response.data.data.code}</p>
                </div>
              </React.Fragment>
            ),
          })
        }
      })
      .catch((thrown) => {});
  }

  onNewMessage(event) {
    const storeState = store.getState();
    const {
      selectedPupil,
    } = storeState;

    if (
      event.detail._source === 'myself' ||
      (
        selectedPupil &&
        event.detail.pupil_id &&
        event.detail.pupil_id !== selectedPupil.id
      )
    ) {
      return;
    }

    this.addSelectedPupilMessagesCount(1);
  }

  onGetThread(event) {
    const storeState = store.getState();
    const {
      selectedPupil,
    } = storeState;

    if (
      !event.detail.thread ||
      (
        selectedPupil &&
        event.detail.pupil_id &&
        event.detail.pupil_id !== selectedPupil.id
      )
    ) {
      return;
    }

    // Substract the current count of messages, since we are setting it to 0
    this.addSelectedPupilMessagesCount(
      -event.detail.thread.new_messages
    );
  }

  onGetPushNotificationMessage(notification, retryCount = 0) {
    const storeState = store.getState();
    const {
      selectedPupil,
      pupils,
    } = storeState;

    // Hacky way around the issue, where we don't have the pupils
    //   loaded in the store state yet.
    if (
      appType === 'parent' &&
      pupils.length === 0 &&
      retryCount < 5
    ) {
      setTimeout(() => {
        this.onGetPushNotificationMessage(notification, retryCount + 1);
      }, 400);

      return;
    }

    axios
      .post(`${window.apiUrl}/pushnotification`, qs.stringify(notification))
      .then((response) => {
        if (!response.data.error) {
          const data = response.data.data;

          // For now only push notifications that open messages & announcement views
          if (
            !data ||
            !data.type ||
            !(
              data.type === 'messages' ||
              data.type === 'announcements' ||
              data.type === 'homework'
            )
          ) {
            return;
          }

          let newSelectedPupil = selectedPupil;

          if (
            data.pupil_id &&
            appType === 'parent' &&
            (
              !newSelectedPupil ||
              newSelectedPupil.id !== data.pupil_id
            )
          ) {
            for (let i = 0; i < pupils.length; i++) {
              if (pupils[i].id === data.pupil_id) {
                newSelectedPupil = pupils[i];
              }
            }
          }

          // If the pupil still isn't found, then ignore
          if (!newSelectedPupil) {
            return;
          }

          newSelectedPupil.defaultView = data.type;

          this.props.setData(newSelectedPupil, 'selectedPupil');

          // Used only for the mobile app, as it doesn't listen to user changes (yet),
          // so we need to tell it that the view changed
          window.dispatchEvent(new CustomEvent(
            'app:change-view',
            {
              bubbles: true,
              detail: data,
            }
          ));
        }
      })
      .catch((throws) => {})
    ;
  }

  addSelectedPupilMessagesCount(amount) {
    const storeState = store.getState();
    const {
      selectedPupil,
      pupils,
    } = storeState;

    let newSelectedPupil = selectedPupil;
    let newPupils = pupils;

    newSelectedPupil.messages_count += amount;

    for (let i = 0; i < newPupils.length; i++) {
      if (newPupils[i].id === selectedPupil.id) {
        newPupils[i].messages_count += amount;
        break;
      }
    }

    store.dispatch(setData(newSelectedPupil, 'selectedPupil'));
    store.dispatch(setData(newPupils, 'pupils'));
  }

  checkLoggedInState(dispatchToStore) {
    const {
      cookies,
      enqueueSnackbar,
    } = this.props;

    const storeState = store.getState();

    const { user, selectedPupil } = storeState;
    const userCredentials = getSessionCredentials(cookies);
    const isLoggedIn = userCredentials !== null && userCredentials.session_id !== null;
    const isPupilSelected = selectedPupil !== null;

    if (isMobileApp) {
      document.body?.classList?.add('with-background');
    }

    if (isLoggedIn) {
      axios.defaults.headers.common.Authorization = `Basic ${userCredentials.session_id}`;
    }

    if (user && user.language) {
      i18n.changeLanguage(user.language);
    }

    if (isPupilSelected) {
      if (isMobileApp) {
        document.body?.classList?.remove('with-background');
      }

      let selectedPupilIndex = appType === 'student' ? 1 : 0;
      storeState.pupils.map((pupil, index) => {
        if (pupil.id === selectedPupil.id) {
          return selectedPupilIndex = index;
        }
      });

      this.setState({
        isLoggedIn: true,
        view: 'pupil',
        selectedPupil,
        selectedPupilIndex,
      });

      this.checkFabButton(selectedPupil);
    } else if (isLoggedIn) {
      const now = moment();
      if (
        user !== null &&
        (
          this.lastPingTime &&
          now.diff(this.lastPingTime, 'milliseconds') < 5000
        )
      ) {
        this.prepareViewState(dispatchToStore, user, null);
      } else {
        this.doPing(dispatchToStore, user);
      }

      // We are on the parent dashboard at this point,
      // if no pupil is selected but we are logged in
      if (appType === 'parent') {
        this.setState({
          selectedPupil: null,
          selectedPupilIndex: 0,
        });
      }
    } else {
      this.lastPingTime = null;
      this.setState({
        isLoggedIn: false,
        view: 'non_logged',
        selectedPupil: null,
        selectedPupilIndex: 0,
      });
    }

    if (
      isLoggedIn &&
      this.pushNotificationData &&
      !this.isPushNotificationRequestDone &&
      !this.isPushNotificationRequestInProgress
    ) {
      this.isPushNotificationRequestInProgress = true;

      axios
        .post(`${window.apiUrl}/device`, qs.stringify({
          device_os: this.pushNotificationData.device_os,
          device_uid: this.pushNotificationData.device_uid,
          push_notification_token: this.pushNotificationData.push_notification_token,
        }))
        .then((data) => {
          this.isPushNotificationRequestInProgress = false;
          this.isPushNotificationRequestDone = true;
        })
        .catch((throws) => {
          this.isPushNotificationRequestInProgress = false;
          this.isPushNotificationRequestDone = true;
        })
        .catch((thrown) => {});
    }
  }

  checkFabButton(selectedPupil) {
    if (
      selectedPupil &&
      selectedPupil.defaultView === 'messages'
    ) {
      document.body?.classList?.add('hide-fab-button');
    } else {
      document.body?.classList?.remove('hide-fab-button');
    }
  }

  updateUserSession(response) {
    const {
      cookies,
    } = this.props;

    if (
      response.data.meta &&
      response.data.meta.session_id
    ) {
      setSessionCredentials(cookies, response.data.meta.session_id);
      axios.defaults.headers.common.Authorization = `Basic ${response.data.meta.session_id}`;
    }

    if (response.data.expired) {
      logoutUser(cookies, this.pushNotificationData);
    }
  }

  doPing(dispatchToStore, user) {
    const {
      t,
      enqueueSnackbar,
    } = this.props;

    const now = moment();

    if(
      this.lastPingTime &&
      now.diff(this.lastPingTime, 'milliseconds') < (this.pingInterval - 1000)
    ) {
      return;
    }

    this.lastPingTime = moment();
    axios
      .post(`${apiUrl}/ping`, qs.stringify({
        include_data: user === null,
      }))
      .then((response) => {
        this.updateUserSession(response);
        this.prepareViewState(dispatchToStore, user, response);

        if (
          this.appVersion &&
          !this.appVersionUpdateInfoShown &&
          response.data.meta?.version !== this.appVersion
        ) {
          enqueueSnackbar(t('New app version available.'), {
            variant: 'info',
            persist: true,
            action: () => {
              return (
                <Button onClick={() => {
                  window.location.reload();
                }} style={{ color: 'white' }}>
                  {t('Reload App')}
                </Button>
              );
            },
          });

          this.appVersionUpdateInfoShown = true;
        }

        this.appVersion = response.data.meta?.version;
      })
      .catch((thrown) => {});
  }

  prepareViewState(dispatchToStore, user, response) {
    const {
      cookies,
      enqueueSnackbar,
    } = this.props;

    if (
      response &&
      response.data.error
    ) {
      logoutUser(cookies, this.pushNotificationData);
      this.setState({
        isLoggedIn: false,
        view: 'non_logged',
        selectedPupil: null,
        selectedPupilIndex: 0,
      });

      enqueueSnackbar(response.data.error, {
        variant: 'error',
        autoHideDuration: 2000,
      });
    } else {
      let defaultView = 'dashboard';
      if (
        response &&
        response.data?.data?.user
      ) {
        user = response.data.data.user;
      }

      if (dispatchToStore) {
        saveUserToStore(user);
        // in the case of the student app, the user itself is the selected student
        if (appType === 'student') {
          const selectedPupilData = user;

          defaultView = getDefaultViewForPupil(selectedPupilData);

          selectedPupilData.defaultView = defaultView;

          if (window.location.hash) {
            const hashSplit = window.location.hash.split(',');
            selectedPupilData.defaultView = hashSplit[1];
          }

          store.dispatch(setData(selectedPupilData, 'selectedPupil'));
        }
      }

      if (this.state.view !== 'pupil') {
        this.setState({
          isLoggedIn: true,
          view: defaultView,
          selectedPupil: null,
          selectedPupilIndex: 0,
        });
      }
    }
  }

  render() {
    // TODO
    // We should be setting those variables into the redux store and using that as the source of truth,
    // instead of prop-drilling those variables down to the children.
    const {
      isLoggedIn,
      selectedPupil,
      selectedPupilIndex,
      view,
      isLogoutDialogOpen,
      isDobDialogOpen,
      isAlertDialogOpen,
      alertDialogContent,
    } = this.state;
    const {
      t,
    } = this.props;

    return (
      <ErrorBoundary location="App.js">
        {isMobileApp && (
          <MobileApp
            isLoggedIn={isLoggedIn}
            selectedPupil={selectedPupil}
            selectedPupilIndex={selectedPupilIndex}
            view={view}
            onDashboardClick={this.onDashboardClick}
            onLogoutClick={this.onLogoutClick}
            onMyCodeClick={this.onMyCodeClick}
          />
        )}
        {!isMobileApp && (
          <DesktopApp
            useSuspense={false}
            isLoggedIn={isLoggedIn}
            selectedPupil={selectedPupil}
            selectedPupilIndex={selectedPupilIndex}
            view={view}
            onDashboardClick={this.onDashboardClick}
            onLogoutClick={this.onLogoutClick}
            onMyCodeClick={this.onMyCodeClick}
          />
        )}
        <LogoutDialog
          useSuspense={false}
          isOpen={isLogoutDialogOpen}
          onClose={this.onLogoutDialogClose}
          onConfirm={this.onLogoutDialogButtonClick}
        />
        <DateOfBirthDialog
          useSuspense={false}
          isOpen={isDobDialogOpen}
          onClose={() => this.setState({
            isDobDialogOpen: false,
          })}
          onConfirm={this.onDateOfBirthConfirm}
        />
        <AlertDialog
          useSuspense={false}
          title={t('My code')}
          isOpen={isAlertDialogOpen}
          onClose={() => this.setState({
            isDobDialogOpen: false,
            isAlertDialogOpen: false,
          })}
        >
          {alertDialogContent}
        </AlertDialog>
      </ErrorBoundary>
    );
  }
}

export default connect(mapStateToProps, mapDispatchToProps)(
  withCookies(withSnackbar(withTranslation()(App)))
);
